import React, { useState } from 'react'

import debounce from 'lodash/debounce'
import tzlookup from 'tz-lookup'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'

type PlaceAttributes = {
  address: string
  postcode: string
  state: string
  city: string
  country: string
  timeZone: string
  lat?: number
  lng?: number
  siret?: string
}

export type Suggestion = {
  label: string
  value: string
}

const getPlaceAttributes = async (
  place: google.maps.places.PlaceResult
): Promise<PlaceAttributes> => {
  let address = place.name || ''
  let postcode = ''
  let city = ''
  let state = ''
  let country = ''
  const siret = ''
  const lat = place.geometry?.location?.lat()
  const lng = place.geometry?.location?.lng()

  for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
    const componentType = component.types[0]

    switch (componentType) {
      case 'street_number': {
        break
      }

      case 'route': {
        address += `, ${component.short_name}`
        break
      }

      case 'postal_code': {
        postcode = `${component.long_name}${postcode}`
        break
      }

      case 'postal_code_suffix': {
        postcode = `${postcode}-${component.long_name}`
        break
      }

      case 'locality':
        address += `, ${component.short_name}`
        city = component.long_name
        break

      case 'administrative_area_level_1': {
        state = component.short_name
        break
      }

      case 'country':
        address += `, ${component.short_name}`
        country = component.short_name.toLowerCase()
        break
    }
  }

  const timeZone = lat && lng ? tzlookup(lat, lng) : ''

  return { address, postcode, state, city, country, lat, lng, timeZone, siret }
}

export const useAutoCompleteAddress = ({
  inputRef,
  placesLibraryLoaded,
}: {
  inputRef: React.MutableRefObject<HTMLInputElement | null>
  placesLibraryLoaded: boolean
}) => {
  const [debouncedValue, setDebouncedValue] = useState<string>('')
  const [place, setPlace] = useState<PlaceAttributes>()
  const {
    access: { locale: currentLocale },
  } = useCurrentSession()
  const locale = currentLocale.split('-')[0] ?? 'en'
  const debouncedOnSearch = useMemo(
    () =>
      debounce((val) => {
        setDebouncedValue(val)
      }, 500),
    []
  )
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])

  useEffect(() => {
    if (!inputRef.current || typeof google === 'undefined' || !placesLibraryLoaded) return

    if (debouncedValue.length === 0) {
      setSuggestions([])
      setPlace(undefined)
    }

    new google.maps.places.AutocompleteService().getPlacePredictions(
      { input: debouncedValue, language: locale },
      (placeSuggestions) => {
        setSuggestions(
          placeSuggestions?.map((suggestion) => ({
            label: suggestion.description,
            value: suggestion.place_id,
          })) || []
        )
      }
    )
  }, [debouncedValue, typeof google])

  const initPlaces = useCallback(() => {
    if (!placesLibraryLoaded) return

    inputRef.current?.addEventListener('input', () => {
      debouncedOnSearch(inputRef.current?.value || '')
    })
  }, [placesLibraryLoaded])

  initPlaces()

  const onPlaceSelected = (placeId: string): void => {
    setSuggestions([])
    const request = { placeId, fields: ['geometry', 'address_components', 'name'] }
    if (!inputRef.current) return

    const placeService = new google.maps.places.PlacesService(inputRef.current)

    placeService.getDetails(
      request,
      async (selectedPlace) => selectedPlace && setPlace(await getPlaceAttributes(selectedPlace))
    )
  }

  return {
    place,
    suggestions,
    onPlaceSelected,
  }
}

interface Props {
  latOrigin: number
  lonOrigin: number
  latDestination: number
  lonDestination: number
  roundTrip: boolean
  units: string
}

const calculateDistance = async ({
  latOrigin,
  lonOrigin,
  latDestination,
  lonDestination,
  roundTrip = false,
  units,
}: Props): Promise<string | undefined> => {
  const origin = new google.maps.LatLng(latOrigin, lonOrigin)
  const destination = new google.maps.LatLng(latDestination, lonDestination)

  const service = new google.maps.DistanceMatrixService()
  try {
    const response = await service.getDistanceMatrix({
      origins: [origin],
      destinations: [destination],
      travelMode: google.maps.TravelMode.DRIVING,
    })

    const distance = response.rows[0]?.elements[0]?.distance.value

    if (!distance) return

    const multiplier = roundTrip ? 2 : 1
    const divider = units == 'mi' ? 1609.34708789 : 1000
    const mileage = ((distance * multiplier) / divider).toFixed(2)

    return mileage
  } catch (error) {
    console.error('Error calculating distance:', error)
    throw error
  }
}
